import React, { createContext } from "react";
import axios from "axios";
import {
  useLoginUserMutation,
  useLogoutQuery,
  useLogoutUserMutation,
  useRegisterUserMutation,
} from "redux_dir/features/userApi";
import { useDispatch, useSelector } from "react-redux";
import { CSRF_TOKEN_PATH } from "routes/api.path";
import { baseApi } from "redux_dir/features/apiMananger";
import {
  addCsrfToken,
  addUserId,
  addUserStatus,
  addBearerToken,
  clearAppelOffline,
  clearUserInfo,
} from "redux_dir/features/userSlice";
import { useNavigate } from "react-router-dom";
import { clearParentForm } from "redux_dir/features/parentSlice";
import mixpanel from "mixpanel-browser";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [loginUser] = useLoginUserMutation();
  const [registerUser] = useRegisterUserMutation();
  const [logoutUser] = useLogoutUserMutation()
  const navigate = useNavigate();

  const from = "/";
  const fromLogout = "/login";

  const dispatch = useDispatch();

  // Hydrate token from localStorage on mount so userInfo.token is filled
  React.useEffect(() => {
    try {
      const stored = localStorage.getItem('auth_token');
      if (stored) {
        dispatch(addBearerToken(stored));
      }
    } catch {}
  }, [dispatch]);

  const [isRegisterLoading, setIsRegisterLoading] = React.useState(false);
  const [isLoginLoading, setIsLoginLoading] = React.useState(false);
  const [loginError, setLoginError] = React.useState("");

  const register = async (form) => {
    setIsRegisterLoading(true);
    try {
      const token = await axios.get(CSRF_TOKEN_PATH);
      const user = await registerUser({
        body: { ...form },
        token: token.data?.csrftoken,
      });

      // console.log({ user });

      if (user?.error?.status === 400) {
        return alert("Cet utilisateur existe deja");
      }

      if (user?.error) {
        return alert("Une erreur c'est produite");
      }

      dispatch(addUserId(user?.data?.id));
      dispatch(addCsrfToken(token.data.csrftoken));
      
      // Stocker le token JWT (Bearer) s'il existe après inscription
      const bearer = user?.data?.access_token || user?.data?.token;
      if (bearer) {
        dispatch(addBearerToken(bearer));
        try { localStorage.setItem('auth_token', bearer); } catch {}
      }
      
      // Mixpanel Tracking : Identifier le nouvel utilisateur
      const newUserId = user?.data?.id;
      if (newUserId) {
        mixpanel.identify(newUserId);
        
        const phoneNum = form?.phone || form?.telephone || form?.numero;
        
        mixpanel.people.set({
          "$phone": phoneNum,
          "$name": user?.data?.firstName ? `${user.data.firstName} ${user.data.lastName}` : phoneNum,
        });
        mixpanel.track("Inscription Réussie");
      }

      setIsRegisterLoading(false);
      navigate(from, { replace: true });
    } catch (error) {
      console.log({ error });
      setIsRegisterLoading(false);
    }
  };

  const login = async (form) => {
    setIsLoginLoading(true);
    try {
      // Récupérer le CSRF token
      const token = await axios.get(CSRF_TOKEN_PATH);
      
      // Appel API login
      const user = await loginUser({
        body: { ...form },
        token: token.data?.csrftoken,
      });

      if (user?.error?.status === 401) {
        setIsLoginLoading(false);
        setLoginError(user.error.data.message);
        return { error: user.error.data.message };
      } else {
        // Extraire les données utilisateur
        const userId = user?.data?.id;
        const userStatus = user?.data?.status;
        
        // Stocker les informations utilisateur dans Redux
        dispatch(addUserId(userId));
        if (userStatus) {
          dispatch(addUserStatus(userStatus));
        }
        dispatch(addCsrfToken(token.data.csrftoken));
        
        // ✅ CORRECTION: Stocker le token JWT
        const bearer = user?.data?.token || user?.data?.access_token;
        if (bearer) {
          dispatch(addBearerToken(bearer));
          try { 
            localStorage.setItem('auth_token', bearer);
          } catch (e) {
            console.error('Erreur localStorage:', e);
          }
        }
        
        // Mixpanel Tracking : Identifier l'utilisateur
        if (userId) {
          mixpanel.identify(userId);
          
          const phoneNum = form?.phone || form?.telephone || form?.numero;
          
          mixpanel.people.set({
            "Statut": userStatus || "Inconnu",
            "$phone": phoneNum,
            "$name": user?.data?.firstName ? `${user.data.firstName} ${user.data.lastName}` : phoneNum,
          });
          mixpanel.track("Connexion Réussie");
        }

        navigate(from, { replace: true });
        setIsLoginLoading(false);
        return { data: user.data };
      }
    } catch (error) {
      console.error('Erreur login:', error);
      setIsLoginLoading(false);
      return { error };
    }
  };

  const logout = async () => {
    try {
      dispatch(clearUserInfo());
      dispatch(clearParentForm());
      dispatch(clearAppelOffline());
      
      // Nettoyer le token du localStorage
      try { localStorage.removeItem('auth_token'); } catch {}
      
      // Ensuite, déconnectez-vous et réinitialisez l'état de l'API
      logoutUser();
      dispatch(baseApi.util.resetApiState());
      
      // Mixpanel Tracking : Réinitialiser la session
      mixpanel.track("Déconnexion");
      mixpanel.reset();

      navigate(from, { replace: true });

    } catch (error) {
      console.log(error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        login,
        isLoginLoading,
        loginError,
        register,
        isRegisterLoading,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
